import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useT } from "@/i18n";
import { PageHeader } from "@/components/StatCard";
import Seo from "@/components/Seo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  Gem, Sparkles, Users, Activity, ChevronRight, Plus, Copy, UserRound,
  MoreVertical, Pencil, Archive, ArchiveRestore, Trash2,
} from "lucide-react";

const HEALTH = {
  green: { dot: "bg-emerald-500", key: "cc.health_active" },
  yellow: { dot: "bg-amber-500", key: "cc.health_quiet" },
  red: { dot: "bg-rose-500", key: "cc.health_silent" },
};
const KIND = {
  local: { key: "cc.kind_local", cls: "bg-emerald-100 text-emerald-700" },
  individual: { key: "cc.kind_individual", cls: "bg-amber-100 text-amber-700" },
  inner: { key: "cc.kind_inner", cls: "bg-teal-100 text-teal-700" },
};

const CARDS = [
  { to: "/app/coach/circles/inner", icon: Gem, titleKey: "ch.i_inner", subKey: "cc.card_inner_sub", testid: "open-inner-circle", cls: "bg-teal-50 text-teal-600" },
  { to: "/app/coach/circles/mein-kreis", icon: Sparkles, titleKey: "ch.i_mein", subKey: "cc.card_mein_sub", testid: "open-mein-kreis", cls: "bg-amber-50 text-amber-600" },
  { to: "/app/coach/circles/local", icon: Users, titleKey: "cc.kind_inner", title: "Local Circle", subKey: "cc.card_local_sub", testid: "open-local-circle", cls: "bg-blue-50 text-blue-600" },
  { to: "/app/coach/client-pulse", icon: Activity, titleKey: "ch.i_pulse", subKey: "cc.card_pulse_sub", testid: "open-client-pulse", cls: "bg-rose-50 text-rose-600" },
];

export default function CoachCircles() {
  const t = useT();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [circles, setCircles] = useState([]);
  const [myClients, setMyClients] = useState({ count: 0, clients: [] });
  const [loading, setLoading] = useState(true);
  const [renameTarget, setRenameTarget] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    if (!user || user.role !== "coach") return;
    Promise.all([
      api.get("/circles").then(({ data }) => (Array.isArray(data) ? data : data.rows || [])).catch(() => []),
      api.get("/coach/circles/my-clients").then(({ data }) => data).catch(() => ({ count: 0, clients: [] })),
    ]).then(([cs, mc]) => { setCircles(cs); setMyClients(mc || { count: 0, clients: [] }); })
      .finally(() => setLoading(false));
  }, [user]);

  if (user && user.role !== "coach") return <Navigate to="/app/circles" replace />;

  const copyCode = (code) => { navigator.clipboard?.writeText(code); toast.success(t("cc.code_copied")); };

  const openRename = (c) => { setRenameTarget(c); setRenameValue(c.name || ""); };

  const submitRename = async () => {
    const name = renameValue.trim();
    if (name.length < 2) { toast.error(t("cc.name_short")); return; }
    setBusyId(renameTarget.id);
    try {
      await api.patch(`/circles/${renameTarget.id}`, { name });
      setCircles((prev) => prev.map((c) => (c.id === renameTarget.id ? { ...c, name } : c)));
      toast.success(t("cc.renamed"));
      setRenameTarget(null);
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusyId(null); }
  };

  const toggleArchive = async (c) => {
    const next = !c.archived;
    setBusyId(c.id);
    try {
      await api.patch(`/circles/${c.id}`, { archived: next });
      setCircles((prev) => prev.map((x) => (x.id === c.id ? { ...x, archived: next } : x)));
      toast.success(next ? t("cc.archived_toast") : t("cc.restored_toast"));
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusyId(null); }
  };

  const confirmDelete = async () => {
    const c = deleteTarget;
    setBusyId(c.id);
    try {
      await api.delete(`/circles/${c.id}`);
      setCircles((prev) => prev.filter((x) => x.id !== c.id));
      toast.success(t("cc.deleted"));
      setDeleteTarget(null);
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusyId(null); }
  };

  // Active circles first, archived last.
  const sorted = [...circles].sort((a, b) => Number(!!a.archived) - Number(!!b.archived));

  return (
    <div>
      <Seo title={t("cc.seo")} noindex />
      <PageHeader eyebrow="Coach" title={t("cc.title")} subtitle={t("cc.subtitle")} />

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {CARDS.map((c) => (
          <Link key={c.to} to={c.to} data-testid={c.testid} className="group flex flex-col rounded-2xl border border-border bg-card p-5 transition-colors hover:bg-accent/40">
            <div className={`flex h-11 w-11 items-center justify-center rounded-full ${c.cls}`}>
              <c.icon className="h-5 w-5" />
            </div>
            <p className="mt-4 font-semibold text-foreground">{c.title || t(c.titleKey)}</p>
            <p className="mt-0.5 text-sm text-muted-foreground">{t(c.subKey)}</p>
            <ChevronRight className="mt-3 h-4 w-4 text-muted-foreground transition-transform group-hover:translate-x-1" />
          </Link>
        ))}
      </div>

      <div className="mt-10 flex items-center justify-between">
        <h2 className="font-display text-xl text-foreground">{t("cc.your_circles")}</h2>
        <Button variant="outline" className="gap-1.5 rounded-full" onClick={() => navigate("/app/circles")} data-testid="new-circle-btn">
          <Plus className="h-4 w-4" /> {t("cc.new_circle")}
        </Button>
      </div>

      {loading ? (
        <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {[0, 1, 2].map((i) => <Skeleton key={i} className="h-28 rounded-2xl" />)}
        </div>
      ) : circles.length === 0 ? (
        <p className="mt-4 rounded-2xl border border-dashed border-border bg-card p-6 text-center text-sm text-muted-foreground">{t("cc.empty")}</p>
      ) : (
        <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {sorted.map((c) => {
            const h = HEALTH[c.health_signal?.level] || null;
            const kind = KIND[c.circle_kind] || KIND.local;
            const total = c.health_signal?.total_members ?? (c.members || []).length;
            const msgs7 = c.health_signal?.messages_7d ?? 0;
            const active7 = c.health_signal?.active_members_7d ?? 0;
            return (
              <div
                key={c.id}
                role="button"
                tabIndex={0}
                onClick={() => navigate(`/app/circles?c=${c.id}`)}
                onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && navigate(`/app/circles?c=${c.id}`)}
                data-testid={`open-circle-${c.id}`}
                className={`cursor-pointer rounded-2xl border border-border bg-card p-4 transition-colors hover:bg-accent ${c.archived ? "opacity-60" : ""}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <p className="min-w-0 font-semibold text-foreground">{c.name}</p>
                  <div className="flex shrink-0 items-center gap-1.5">
                    {c.archived && <span className="rounded-full bg-secondary px-2 py-0.5 text-[10px] font-semibold text-muted-foreground" data-testid={`circle-archived-${c.id}`}>{t("cc.archived")}</span>}
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${kind.cls}`} data-testid={`circle-kind-${c.id}`}>{t(kind.key)}</span>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                        <button
                          type="button"
                          className="rounded-full p-1 text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
                          data-testid={`circle-menu-${c.id}`}
                          aria-label={t("cc.actions")}
                        >
                          <MoreVertical className="h-4 w-4" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                        <DropdownMenuItem onClick={() => openRename(c)} data-testid={`circle-rename-${c.id}`}>
                          <Pencil className="mr-2 h-4 w-4" /> {t("cc.rename")}
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => toggleArchive(c)} disabled={busyId === c.id} data-testid={`circle-archive-${c.id}`}>
                          {c.archived
                            ? <><ArchiveRestore className="mr-2 h-4 w-4" /> {t("cc.restore")}</>
                            : <><Archive className="mr-2 h-4 w-4" /> {t("cc.archive")}</>}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => setDeleteTarget(c)} className="text-rose-600 focus:text-rose-600" data-testid={`circle-delete-${c.id}`}>
                          <Trash2 className="mr-2 h-4 w-4" /> {t("cc.delete")}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
                {h && (
                  <div className="mt-2 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground" data-testid={`health-signal-${c.id}`}>
                    <span className={`h-2 w-2 rounded-full ${h.dot}`} />
                    <span className="font-medium text-foreground">{t(h.key)}</span>
                    <span>· {t("cc.health_line", { total, msgs: msgs7, active: active7 })}</span>
                  </div>
                )}
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); copyCode(c.invitation_code); }}
                  data-testid={`copy-code-${c.id}`}
                  className="mt-2 inline-flex items-center gap-1 rounded-full bg-secondary px-2 py-0.5 text-[11px] font-medium text-foreground transition-colors hover:bg-accent"
                  title={t("cc.copy_code")}
                >
                  <Copy className="h-3 w-3" /> {c.invitation_code}
                </button>
              </div>
            );
          })}
        </div>
      )}

      <div className="mt-10">
        <div className="flex items-center gap-2">
          <h2 className="font-display text-xl text-foreground">{t("cc.my_clients")}</h2>
          <span className="rounded-full bg-secondary px-2 py-0.5 text-xs font-medium text-muted-foreground" data-testid="my-clients-count">{myClients.count || 0}</span>
        </div>
        {(myClients.clients || []).length === 0 ? (
          <p className="mt-4 rounded-2xl border border-dashed border-border bg-card p-6 text-center text-sm text-muted-foreground">{t("cc.no_clients")}</p>
        ) : (
          <div className="mt-4 space-y-2">
            {myClients.clients.map((cl) => (
              <div key={cl.id} data-testid={`my-client-${cl.id}`} className="flex items-center gap-3 rounded-xl border border-border bg-card p-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-accent text-accent-foreground">
                  <UserRound className="h-4 w-4" />
                </div>
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-foreground">{cl.name || cl.email || t("cc.client")}</p>
                  {cl.email && <p className="truncate text-xs text-muted-foreground">{cl.email}</p>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Rename dialog */}
      <Dialog open={!!renameTarget} onOpenChange={(o) => !o && setRenameTarget(null)}>
        <DialogContent data-testid="circle-rename-dialog">
          <DialogHeader>
            <DialogTitle>{t("cc.rename_title")}</DialogTitle>
            <DialogDescription>{t("cc.rename_desc")}</DialogDescription>
          </DialogHeader>
          <Input
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && submitRename()}
            placeholder={t("cc.rename_ph")}
            autoFocus
            data-testid="circle-rename-input"
          />
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setRenameTarget(null)}>{t("cc.cancel")}</Button>
            <Button className="rounded-full" onClick={submitRename} disabled={busyId === renameTarget?.id} data-testid="circle-rename-save">{t("cc.save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="circle-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("cc.delete_title", { name: deleteTarget?.name || "" })}</AlertDialogTitle>
            <AlertDialogDescription>{t("cc.delete_desc")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="circle-delete-cancel">{t("cc.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-rose-600 hover:bg-rose-700" data-testid="circle-delete-confirm">
              {t("cc.delete_confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
